import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_PassportPhotoMaker = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/passport-photo-maker',
			name: 'Passport Photo Maker — Create Passport Size Photo Online | Lift',
			description:
				'Generate passport size photo instantly with AI. No watermark. Upload photo, resize to perfect passport size with white background and centered face in one click. Try now!',
			url: 'https://liftapp.ai/passport-photo-maker',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/passport-photo-maker#product' },
				{ '@id': 'https://liftapp.ai/passport-photo-maker#howto' },
				{ '@id': 'https://liftapp.ai/passport-photo-maker#benefits' },
				{ '@id': 'https://liftapp.ai/passport-photo-maker#faq' },
				{ '@id': 'https://liftapp.ai/passport-photo-maker#why-lift' },
				{ '@id': 'https://liftapp.ai/passport-photo-maker#explore-tools' },
				{ '@id': 'https://liftapp.ai/passport-photo-maker#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/passport-photo-maker#product',
			name: 'Passport Photo Maker Online',
			description:
				'Create passport size photo in one second with AI. Get a compliant passport photo with white background, perfect sizing, and centered portrait, no watermarks.',
			url: 'https://liftapp.ai/passport-photo-maker',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/passport-photo-maker#howto',
			name: 'Let the AI do the work',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift resizes, crops, centers your face, and adds white background automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Download your passport photo',
					text: 'Download your compliant passport photo ready for official documents',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/passport-photo-maker#benefits',
			name: 'Who can benefit from Passport Photo Maker?',
			description:
				"Lift's Passport Photo Maker is useful for job seekers, students, travelers, businesses, and anyone who needs compliant passport photos for official documents.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job seekers & professionals — Get compliant passport and visa photos fast for job applications and official documents. One-click processing ensures correct size, white background, and face centering without visiting a studio.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Students & educators — Perfect for visa applications, exam registrations, university IDs, and student documents. Create regulation-ready passport photos online with no technical skills or extra costs required.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Travelers — Need urgent passport or visa photos while abroad or planning your trip? Works from any photo on any device, delivering centered, correctly sized images with white backgrounds in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small businesses & HR teams — Generate standardized employee photos for ID badges, internal systems, and company documents. Automatic background removal and resizing ensure consistency across your team with minimal effort.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday documents — Perfect for IDs, membership cards, professional profiles, and official forms. Get simple, fast, and professional passport size photos without Photoshop skills or hiring photographers.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Passport Photo Maker by Lift',
				description:
					'Example of how Lift Passport Photo Maker creates compliant passport photos with white background and perfect sizing.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/passport-photo-maker#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Passport Photo Maker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Passport Photo Maker is a premium online tool that instantly creates passport size photos with white background and centered face positioning — ideal for visa applications, IDs, official documents, or professional profiles.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Passport Photo Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's passport photo maker tool. Our AI automatically detects your face, removes the background, replaces it with white, and resizes to perfect passport dimensions in seconds — no photo editing experience needed. Download up to 20 passport photos with regulation-compliant sizing.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Passport Photo Maker uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/passport-photo-maker#why-lift',
			name: "Why Lift's Passport Photo Maker stands out",
			description:
				"Lift's Passport Photo Maker combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift Passport Photo Maker',
				description:
					"Comparison of Lift's Passport Photo Maker benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/passport-photo-maker#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/passport-photo-maker#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Passport Photo Maker',
					item: 'https://liftapp.ai/passport-photo-maker',
				},
			],
		},
	],
};
